/**
 * VÍDEO 5 — DADO CHAVE
 * "72% do orçamento de tráfego é desperdiçado"
 * 10s | 720×900 | SEM ÁUDIO
 * Copy: BoardCopy (Hormozi + Sugarman)
 */

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  Easing,
  Sequence,
} from "remotion";
import {CLAMP} from "../../presets/idv";

const T = {
  light: { bg: "#F7F6F2", headline: "#1A1A1A", body: "#4A4A4A", muted: "#999999" },
  accent: "#E84545",
  accentBg: "rgba(232,69,69,0.15)",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";

const SZ = { top: 70, sides: 50, bottom: 80 };
const CW = 720 - SZ.sides * 2;

const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });
const scaleIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0.85, 1], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

const NumberHero: React.FC<{value: string; delay?: number}> = ({value, delay = 0}) => {
  const frame = useCurrentFrame();
  const glow = 20 + Math.sin(frame * 0.05) * 8;
  return (
    <div style={{marginBottom: 16}}>
      <div style={{
        opacity: fadeIn(frame, delay, delay + 15),
        transform: `scale(${scaleIn(frame, delay, delay + 10)})`,
        fontFamily: FONT_SERIF, fontSize: 88, fontWeight: 800, color: T.accent, lineHeight: 1,
        textShadow: `0 0 ${glow}px rgba(232,69,69,0.3)`,
      }}>{value}</div>
      <div style={{
        width: interpolate(frame, [delay + 12, delay + 22], [0, 40], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)}),
        height: 3, background: T.accent, marginTop: 12,
      }} />
    </div>
  );
};

const Headline: React.FC<{children: React.ReactNode; size?: number; color: string; delay?: number}> =
  ({children, size = 50, color, delay = 0}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF, fontSize: size, fontWeight: 700, lineHeight: 1.15, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const Body: React.FC<{children: React.ReactNode; color: string; delay?: number}> = ({children, color, delay = 15}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 400, lineHeight: 1.4, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const Credit: React.FC<{text: string; delay?: number}> = ({text, delay = 35}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 10) * 0.7,
      fontFamily: FONT_MONO, fontSize: 12, fontWeight: 400, color: T.light.muted, marginTop: 16,
    }}>{text}</div>
  );
};

const HighlightText: React.FC<{children: React.ReactNode}> = ({children}) => (
  <span style={{background: T.accentBg, padding: "2px 6px", borderRadius: 3}}>{children}</span>
);

const Footer: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{position: "absolute", bottom: 40, left: SZ.sides, right: SZ.sides, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: fadeIn(frame, 40, 50)}}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 400, color: T.light.muted}}>@guilhermeribeiro.ai</span>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 500, color: T.light.muted}}>5/9</span>
    </div>
  );
};

const SeriesLabel: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{position: "absolute", top: 30, left: SZ.sides, opacity: fadeIn(frame, 0, 10), fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: T.light.muted}}>SISTEMA DE MARKETING</div>
  );
};

export const Video05DadoChave: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.light.bg}}>
    <Sequence from={0} durationInFrames={300}>
      <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
        <SeriesLabel />
        <NumberHero value="72%" delay={5} />
        <Body color={T.light.body} delay={20}>
          do orçamento de {" "}<HighlightText>tráfego é desperdiçado</HighlightText> sem funil estruturado.
        </Body>
        <Credit text="Benchmark Dimus, 2024-2026" />
        <Footer />
      </AbsoluteFill>
    </Sequence>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
